import type { ReactNode } from "react";
import { SOLAR } from "@/constants/testIds";

export const EYEBROW = "font-sans text-[0.7rem] uppercase tracking-[0.3em] text-[#8a6f52]";
export const BTN_PRIMARY =
  "rounded-full bg-[#c0501b] px-6 py-2.5 font-sans text-sm uppercase tracking-widest text-[#f6efe3] transition hover:bg-[#a63f12] disabled:opacity-60";
export const BTN_OUTLINE =
  "rounded-full border border-[#2b1f14] px-6 py-2.5 font-sans text-sm uppercase tracking-widest text-[#2b1f14] transition hover:bg-[#2b1f14] hover:text-[#f6efe3]";
export const UNDERLINE_INPUT =
  "w-full border-b border-[#8a6f52] bg-transparent px-1 py-2 font-serif text-xl tabular-nums outline-none focus:border-[#c0501b]";

export const coordLabel = (lat: number, lon: number) => `${lat.toFixed(4)}°, ${lon.toFixed(4)}°`;

export function Row({ label, children, testid }: { label: string; children: ReactNode; testid?: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 lg:justify-end">
      <span className={EYEBROW}>{label}</span>
      <span data-testid={testid} className="font-serif text-lg tabular-nums md:text-xl">
        {children}
      </span>
    </div>
  );
}

export function Stat({ label, value, caption, testid }: { label: string; value: string; caption: string; testid?: string }) {
  return (
    <div className="border-l border-[#d9c9ad] pl-5">
      <p className={EYEBROW}>{label}</p>
      <p data-testid={testid} className="mt-2 font-serif text-3xl tabular-nums">
        {value}
      </p>
      <p className="mt-2 font-sans text-xs italic text-[#8a6f52]">{caption}</p>
    </div>
  );
}

const SUN_EVENT_ALIGN = { left: "md:text-left", center: "md:text-center", right: "md:text-right" };

export function SunEvent({
  label,
  value,
  testid,
  sub,
  subTestid,
  align = "left",
}: {
  label: string;
  value: string;
  testid?: string;
  sub?: string;
  subTestid?: string;
  align?: keyof typeof SUN_EVENT_ALIGN;
}) {
  return (
    <div className={SUN_EVENT_ALIGN[align]}>
      <p className={EYEBROW}>{label}</p>
      <p data-testid={testid} className="mt-2 font-serif text-3xl tabular-nums md:text-4xl">
        {value}
      </p>
      {sub && (
        <p data-testid={subTestid} className="mt-1 font-sans text-xs italic tabular-nums text-[#8a6f52]">
          {sub}
        </p>
      )}
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block flex-1">
      <span className={EYEBROW}>{label}</span>
      {children}
    </label>
  );
}

export function ErrorText({ message }: { message: string }) {
  if (!message) return null;
  return (
    <p data-testid={SOLAR.errorMsg} className="mt-4 font-sans text-sm text-[#8c1a1a]">
      {message}
    </p>
  );
}
